import type { OnClickCallback } from '@atlaskit/editor-common/card/cardOptions';
import { isSSR } from '@atlaskit/editor-common/is-ssr';
import type { CardProvider } from '@atlaskit/editor-common/provider-factory/card-provider';
import type { LinkPickerOptions } from '@atlaskit/editor-common/types/hyperlink';
import type { CardPluginOptions } from '@atlaskit/editor-plugin-card/types';
import type { CardContext } from '@atlaskit/link-provider/types';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import type { FullPageEditorAppearance } from '../types';

// SECTION: From confluence/next/packages/editor-features/src/utils/smartCardOptions.ts
/**
 * Supported Jira URLs and Jira Roadmap URLs, GDrive and ODFB will become Smart Links
 * (only supports Cloud single-issue URLs right now)
 */
const SMART_LINKS_BEFORE_MACROS =
	'jira,jiraroadmap,google-drive-sheets,google-drive-docs,OneDrive,google-drive-slides'.split(',');
// END SECTION

interface Props {
	options: {
		__livePage: boolean | undefined;
		CompetitorPrompt?: React.ComponentType<{ linkType?: string; sourceUrl: string }>;
		editorAppearance: FullPageEditorAppearance;
		enablePasteDisplayAsMenu?: boolean;
		linkPicker: LinkPickerOptions | undefined;
		onClickCallback: OnClickCallback | undefined;
		smartCardContext: CardContext | undefined;
	};
	providers: {
		cardProvider: Promise<CardProvider> | undefined;
	};
}

declare global {
	interface Window {
		__SSR_RENDERED__?: boolean;
	}
}

/**
 *
 * @param root0
 * @param root0.options
 * @param root0.providers
 * @example
 */
export function cardPluginOptions({ options, providers }: Props): CardPluginOptions {
	return {
		smartCardContext: options.smartCardContext,
		// From confluence/next/packages/full-page-editor/src/FullPageEditorComponent.tsx `CARD_OPTIONS =`
		allowDatasource: true,
		// END SECTION
		// SECTION: From confluence/next/packages/editor-features/src/utils/smartCardOptions.ts
		provider: providers.cardProvider,
		resolveBeforeMacros: SMART_LINKS_BEFORE_MACROS,
		allowBlockCards: true,
		allowEmbeds: true,
		// END SECTION
		onClickCallback: options.onClickCallback,
		fullWidthMode: options.editorAppearance === 'full-width',
		linkPicker: options.linkPicker,
		// SECTION: From confluence/next/packages/editor-features/src/hooks/useEditorFeatureFlags.ts
		lpLinkPicker: true,
		// END SECTION
		editorAppearance: options.editorAppearance,
		// @ts-ignore Temporary solution to check for Live Page editor.
		__livePage: options.__livePage,
		isPageSSRed: Boolean(isSSR() || window.__SSR_RENDERED__),
		CompetitorPrompt: options.CompetitorPrompt,
		enablePasteDisplayAsMenu: ['hasSpellingAndGrammar', 'hasAltAiActions'].includes(
			expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control'),
		),
	};
}
